import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Sparkles, Globe, MessageSquare, Star, Bot, TrendingUp,
  Server, Zap, BarChart3, Megaphone, Save, Check, X, Filter,
} from "lucide-react";

const CATEGORY_ICONS = {
  website: Globe,
  communication: MessageSquare,
  reputation: Star,
  ai: Bot,
  seo: TrendingUp,
  infrastructure: Server,
  growth: Zap,
  reporting: BarChart3,
  advertising: Megaphone,
};

const TIER_LABELS = {
  1: { label: "Tier 1 · Must-haves", color: "#0F2340", accent: "#E24E1B" },
  2: { label: "Tier 2 · Growth", color: "#0F2340", accent: "#38a169" },
  3: { label: "Tier 3 · Managed", color: "#0F2340", accent: "#805ad5" },
};

export default function AddOns() {
  const [addons, setAddons] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | tier1 | tier2 | tier3
  const [editing, setEditing] = useState({}); // { addon_id: {price, setup_fee} }

  const load = async () => {
    try {
      const { data } = await api.get("/addons/catalog");
      setAddons(data.addons || []);
      setCategories(data.categories || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return addons;
    const tier = parseInt(filter.replace("tier", ""), 10);
    return addons.filter((a) => a.tier === tier);
  }, [addons, filter]);

  const grouped = useMemo(() => {
    const m = {};
    for (const a of filtered) {
      const t = a.tier || 3;
      (m[t] ||= []).push(a);
    }
    return m;
  }, [filtered]);

  const totalMonthly = useMemo(
    () => addons.filter((a) => a.active).reduce((s, a) => s + (a.price || 0), 0),
    [addons]
  );

  const saveEdits = async (addon_id) => {
    const patch = editing[addon_id];
    if (!patch) return;
    try {
      const { data } = await api.patch(`/addons/catalog/${addon_id}`, patch);
      setAddons((rows) => rows.map((r) => (r.id === addon_id ? data : r)));
      setEditing((e) => { const c = { ...e }; delete c[addon_id]; return c; });
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (addon) => {
    try {
      const { data } = await api.patch(`/addons/catalog/${addon.id}`, { active: !addon.active });
      setAddons((rows) => rows.map((r) => (r.id === addon.id ? data : r)));
      toast.success(data.active ? "Add-on is now available" : "Add-on hidden from catalog");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-10" data-testid="addons-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-2">CRM &amp; growth</div>
          <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-[#E24E1B]" /> Add-Ons
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            The revenue expansion catalog. Toggle add-ons on/off, adjust prices, and activate them per client from the client detail page.
          </p>
        </div>
        <div className="text-right">
          <div className="overline">Max add-on MRR potential</div>
          <div className="font-display text-3xl font-bold text-[#002FA7]">
            ${totalMonthly.toLocaleString()}
            <span className="text-sm font-mono text-neutral-500 ml-1">/mo per client</span>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter className="w-4 h-4 text-neutral-400" />
        {[
          { k: "all", label: "All" },
          { k: "tier1", label: "Tier 1 · Must-haves" },
          { k: "tier2", label: "Tier 2 · Growth" },
          { k: "tier3", label: "Tier 3 · Managed" },
        ].map((f) => (
          <button
            key={f.k}
            data-testid={`filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-widest border transition ${
              filter === f.k
                ? "bg-[#0F2340] text-white border-[#0F2340]"
                : "bg-white text-neutral-600 border-neutral-300 hover:border-neutral-500"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-neutral-500">Loading catalog…</div>
      ) : (
        Object.keys(grouped).sort().map((tier) => (
          <div key={tier} className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-1 h-6"
                style={{ background: TIER_LABELS[tier]?.accent || "#999" }}
              />
              <h2 className="font-display text-xl font-bold">{TIER_LABELS[tier]?.label || `Tier ${tier}`}</h2>
              <span className="text-xs text-neutral-500 font-mono">{grouped[tier].length} items</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[tier].map((a) => {
                const Icon = CATEGORY_ICONS[a.category] || Sparkles;
                const isEditing = editing[a.id] != null;
                const patch = editing[a.id] || {};
                return (
                  <div
                    key={a.id}
                    data-testid={`addon-card-${a.id}`}
                    className={`bg-white border p-5 flex flex-col ${
                      a.active ? "border-neutral-200" : "border-dashed border-neutral-300 opacity-60"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#0F2340] text-white flex items-center justify-center">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="overline text-neutral-500">{categories[a.category] || a.category}</div>
                      </div>
                      <button
                        onClick={() => toggleActive(a)}
                        data-testid={`toggle-${a.id}`}
                        className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 border ${
                          a.active
                            ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                            : "border-neutral-300 text-neutral-500 bg-neutral-50"
                        }`}
                      >
                        {a.active ? "Available" : "Hidden"}
                      </button>
                    </div>
                    <div className="font-semibold text-neutral-900 mb-1">{a.name}</div>
                    <p className="text-sm text-neutral-600 leading-relaxed mb-4 flex-1">{a.tagline}</p>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <PriceField
                        label="Setup"
                        prefix="$"
                        value={isEditing ? (patch.setup_fee ?? a.setup_fee ?? 0) : (a.setup_fee ?? 0)}
                        editing={isEditing}
                        onChange={(v) => setEditing((e) => ({ ...e, [a.id]: { ...(e[a.id] || {}), setup_fee: v } }))}
                        testId={`price-setup-${a.id}`}
                      />
                      <PriceField
                        label="Monthly"
                        prefix="$"
                        suffix="/mo"
                        value={isEditing ? (patch.price ?? a.price) : a.price}
                        editing={isEditing}
                        onChange={(v) => setEditing((e) => ({ ...e, [a.id]: { ...(e[a.id] || {}), price: v } }))}
                        testId={`price-monthly-${a.id}`}
                      />
                    </div>

                    <div className="flex gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveEdits(a.id)}
                            className="flex-1 lf-btn-primary text-xs py-2 flex items-center justify-center gap-1"
                            data-testid={`save-${a.id}`}
                          >
                            <Save className="w-3 h-3" /> Save
                          </button>
                          <button
                            onClick={() => setEditing((e) => { const c = { ...e }; delete c[a.id]; return c; })}
                            className="lf-btn-outline text-xs py-2 flex items-center justify-center gap-1 px-3"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setEditing((e) => ({ ...e, [a.id]: {} }))}
                          className="flex-1 lf-btn-outline text-xs py-2"
                          data-testid={`edit-price-${a.id}`}
                        >
                          Edit price
                        </button>
                      )}
                    </div>

                    {a.site_effect && a.site_effect.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap gap-1">
                        <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-mono w-full mb-1">
                          Adds to site:
                        </span>
                        {a.site_effect.map((s) => (
                          <span key={s} className="text-[10px] font-mono bg-neutral-100 border border-neutral-200 px-1.5 py-0.5">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <div className="mt-8 p-6 bg-neutral-50 border border-neutral-200">
        <div className="flex items-start gap-3">
          <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div>
            <div className="font-semibold text-neutral-900 mb-1">How to activate for a client</div>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Open any client under <a href="/clients" className="underline">Clients</a>, then use the <strong>Add-Ons</strong> tab
              to activate individual items. Website add-ons (extra pages, gallery, etc.) auto-regenerate the client&apos;s preview site.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceField({ label, prefix, suffix, value, editing, onChange, testId }) {
  return (
    <div className="bg-neutral-50 border border-neutral-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-0.5">{label}</div>
      {editing ? (
        <div className="flex items-baseline gap-0.5">
          <span className="text-neutral-500 text-sm">{prefix}</span>
          <input
            type="number"
            min="0"
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-full bg-transparent border-b border-neutral-400 text-sm font-mono font-semibold focus:outline-none focus:border-[#E24E1B]"
            data-testid={testId}
          />
          {suffix && <span className="text-xs text-neutral-500">{suffix}</span>}
        </div>
      ) : (
        <div className="text-sm font-mono font-semibold" data-testid={testId}>
          {prefix}{value}{suffix || ""}
        </div>
      )}
    </div>
  );
}
